import { calculateEntradaTotals } from './flujoCalculations';
import { normalizeBackendDate } from './dateFormatters';

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const toIdentifier = (value) => value === undefined || value === null ? '' : String(value);

const readNestedName = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }

  return firstDefined(value.nombre, value.name, value.descripcion, value.description);
};

export const unwrapFlujosList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.rows)) {
    return response.rows;
  }

  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }

  if (Array.isArray(response?.data?.rows)) {
    return response.data.rows;
  }

  return [];
};

const mapPaletsEntrada = (row) => {
  const source = firstDefined(row.palets_entrada, row.paletsEntrada, row.palets) || {};

  if (Array.isArray(source)) {
    return source.reduce((acc, item) => {
      const idCajaPalet = firstDefined(item.id_caja_palet, item.idCajaPalet);

      if (idCajaPalet) {
        acc[idCajaPalet] = toNumber(firstDefined(item.cantidad, item.value));
      }

      return acc;
    }, {});
  }

  return source;
};

const readClassification = (row, key, kilosFallbackKeys = [], palotsFallbackKeys = []) => {
  const source = firstDefined(row.clasificacion?.[key], row[key]);

  return {
    kilos: toNumber(firstDefined(source?.kilos, ...kilosFallbackKeys.map((field) => row[field]))),
    palots: toNumber(firstDefined(source?.palots, ...palotsFallbackKeys.map((field) => row[field]))),
  };
};

export const normalizeFlujosEntrada = (row, options = {}) => {
  const cajasPalet = options.cajasPalet || [];
  const idAlmacen = toNumber(firstDefined(row.id_almacen, row.idAlmacen, row.almacen?.id));
  const idVariedad = toIdentifier(firstDefined(
    row.codigo_variedad,
    row.codigoVariedad,
    row.variedad?.code,
    row.variedad?.codigo
  ));
  const catalogWarehouse = options.almacenes?.find((almacen) => almacen.id === idAlmacen);
  const catalogVariety = options.variedades?.find((variedad) => String(variedad.id) === idVariedad);
  const paletsEntrada = mapPaletsEntrada(row);
  const pesoPalotId = firstDefined(row.id_peso_palot, row.idPesoPalot, row.peso_palot?.id);
  const pesoCajaId = firstDefined(row.id_peso_caja, row.idPesoCaja, row.peso_caja_ref?.id);
  const precalibrado = readClassification(
    row,
    'precalibrado',
    ['otros_calibres', 'otrosCalibres', 'precalibrado'],
    ['p_otros_calibres', 'pOtrosCalibres', 'palots_precalibrado']
  );
  const retorno = readClassification(row, 'retorno', ['retorno'], ['p_retorno', 'pRetorno', 'palots_retorno']);
  const verde = readClassification(row, 'verde', ['verde'], ['p_verde', 'pVerde', 'palots_verde']);
  const segunda = readClassification(row, 'segunda', ['segunda'], ['p_segunda', 'pSegunda', 'palots_segunda']);
  const bajoCalibre = readClassification(
    row,
    'bajo_calibre',
    ['bajo_calibre', 'bajoCalibre'],
    ['p_bajo_calibre', 'pBajoCalibre', 'palots_bajo_calibre']
  );
  const industria = readClassification(row, 'industria', ['industria'], ['p_industria', 'pIndustria', 'palots_industria']);
  const podrido = readClassification(row, 'podrido', ['podrido'], []);

  const entrada = {
    id: firstDefined(row.id, row.codigo),
    codigo: firstDefined(row.codigo, row.id),
    version: row.version,
    idAlmacen,
    idUsuarioCreador: firstDefined(row.id_usuario_creador, row.idUsuarioCreador, row.usuario_creador?.id),
    usuarioCreador: firstDefined(
      readNestedName(row.usuario_creador),
      readNestedName(row.usuarioCreador),
      row.usuario_creador_nombre
    ),
    fecha: normalizeBackendDate(firstDefined(row.fecha, row.date, '')),
    variedad: firstDefined(readNestedName(row.variedad), row.variedad_nombre, row.nombre_variedad, catalogVariety?.nombre, ''),
    idVariedad,
    almacen: firstDefined(readNestedName(row.almacen), row.almacen_nombre, catalogWarehouse?.nombre, ''),
    kilos: toNumber(row.kilos),
    crucesPalots: toNumber(firstDefined(row.grupos_4_palots, row.cruces_palots, row.crucesPalots)),
    idPesoPalot: pesoPalotId === undefined ? null : toNumber(pesoPalotId),
    kilosPalot: toNumber(firstDefined(row.kilos_palot, row.kilosPalot, row.peso_palot?.peso, row.pesoPalot?.peso)),
    idPesoCaja: pesoCajaId === undefined ? null : toNumber(pesoCajaId),
    pesoCaja: toNumber(firstDefined(row.peso_caja, row.pesoCaja, row.peso_caja_ref?.peso, row.pesoCajaRef?.peso)),
    paletsEntrada,
    otrosCalibres: precalibrado.kilos,
    pOtrosCalibres: precalibrado.palots,
    retorno: retorno.kilos,
    pRetorno: retorno.palots,
    verde: verde.kilos,
    pVerde: verde.palots,
    segunda: segunda.kilos,
    pSegunda: segunda.palots,
    bajoCalibre: bajoCalibre.kilos,
    pBajoCalibre: bajoCalibre.palots,
    industria: industria.kilos,
    pIndustria: industria.palots,
    podrido: podrido.kilos,
    observaciones: firstDefined(row.observaciones, ''),
  };

  const calculated = calculateEntradaTotals(entrada, cajasPalet);

  return {
    ...entrada,
    kilosVolcados: toNumber(firstDefined(row.kilos_volcados, row.kilosVolcados, calculated.kilosVolcados)),
    kgReales: toNumber(firstDefined(row.kg_reales, row.kgReales, calculated.kgReales)),
    kilosClasificados: toNumber(firstDefined(row.kilos_clasificados, row.kilosClasificados, calculated.kilosClasificados)),
    kilosSinClasificar: toNumber(firstDefined(row.kilos_sin_clasificar, row.kilosSinClasificar, calculated.kilosSinClasificar)),
    rendimiento: toNumber(firstDefined(row.rendimiento, calculated.rendimiento)),
    totalPalots: toNumber(firstDefined(row.total_palots, row.totalPalots, calculated.totalPalots)),
    primera: toNumber(firstDefined(row.primera, row.kilos_primera, row.kilosPrimera, calculated.kilosPrimera)),
    porcentajePrimera: toNumber(firstDefined(row.porcentaje_primera, row.porcentajePrimera, calculated.percentages.primera)),
  };
};

export const normalizeFlujosEntradas = (response, options = {}) =>
  unwrapFlujosList(response).map((row) => normalizeFlujosEntrada(row, options));
